import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";

/**
 * GlobalLayoutSlice manages app-level layout and overlay state.
 *
 * **Purpose:**
 * This slice is for UI state that needs to be:
 * - Accessible from multiple distant components across the app
 * - Controlled by global actions (keyboard shortcuts, external integrations)
 * - Rendered once at the app level rather than duplicated in multiple places
 * - Visible in Redux DevTools for debugging and time-travel
 *
 * **What belongs here:**
 * - Global modals/dialogs that can be triggered from anywhere (e.g., SearchableDialPad)
 * - App-wide overlays (command palette, global search, spotlight)
 * - Toast/notification queue management
 * - Global keyboard shortcut state
 * - Guided tour/onboarding overlay state
 *
 * **What does NOT belong here:**
 * - Feature-specific UI state (use feature slices like EmailUISlice, AgentDesktopUISlice)
 * - Page navigation state (use router or AgentDesktopUISlice)
 * - Persisted user preferences (use InputPersistSlice or AgentInformationSlice)
 * - Simple local component state (use useState)
 *
 * **Pattern for adding new global layout concerns:**
 * 1. Add a new nested property to GlobalLayoutState (e.g., `commandPalette: { isOpen: boolean }`)
 * 2. Create actions to control the state (e.g., `openCommandPalette`, `closeCommandPalette`)
 * 3. Create selectors for reading the state (e.g., `selectCommandPaletteState`)
 * 4. Render the component at app level in AppLayout.tsx
 * 5. Create a custom hook for easier consumption (e.g., `useGlobalCommandPalette`)
 *
 * **Example future additions:**
 * ```typescript
 * commandPalette: {
 *   isOpen: boolean;
 *   query?: string;
 * };
 * notifications: {
 *   items: Array<{ id: string; message: string; type: 'info' | 'success' | 'error' }>;
 * };
 * globalSearch: {
 *   isOpen: boolean;
 *   filters?: Record<string, unknown>;
 * };
 * ```
 */
export type GlobalLayoutState = {
    dialpad: {
        /** Whether the dialpad modal is currently visible */
        isOpen: boolean;
        /** Optional phone number to pre-populate in the dialpad search */
        defaultPhoneNumber?: string;
        /** Context tracking where the dialpad was opened from for analytics */
        context?: "navbar" | "task-interface" | "external-integration";
    };
    // Future global layout concerns can be added here
};

const initialState: GlobalLayoutState = {
    dialpad: {
        isOpen: false,
    },
};

const GlobalLayoutSlice = createSlice({
    name: "globalLayout",
    initialState,
    reducers: {
        /**
         * Opens the global SearchableDialPad modal.
         * @param action.payload.defaultPhoneNumber - Optional phone number to pre-populate
         * @param action.payload.context - Where the dialpad was opened from (for analytics)
         */
        openDialpad(
            state,
            action: PayloadAction<{
                defaultPhoneNumber?: string;
                context?: GlobalLayoutState["dialpad"]["context"];
            }>
        ) {
            state.dialpad.isOpen = true;
            state.dialpad.defaultPhoneNumber = action.payload.defaultPhoneNumber;
            state.dialpad.context = action.payload.context;
        },
        /**
         * Closes the global SearchableDialPad modal and clears its state.
         */
        closeDialpad(state) {
            state.dialpad.isOpen = false;
            state.dialpad.defaultPhoneNumber = undefined;
            state.dialpad.context = undefined;
        },
    },
});

export const { openDialpad, closeDialpad } = GlobalLayoutSlice.actions;
export default GlobalLayoutSlice.reducer;

// Selectors
export const selectGlobalLayout = (state: RootState): GlobalLayoutState => state.globalLayout;

export const selectDialpadState = createSelector(selectGlobalLayout, (globalLayout) => globalLayout.dialpad);

export const selectIsDialpadOpen = createSelector(selectDialpadState, (dialpad) => dialpad.isOpen);

export const selectDialpadDefaultPhone = createSelector(selectDialpadState, (dialpad) => dialpad.defaultPhoneNumber);

export const selectDialpadContext = createSelector(selectDialpadState, (dialpad) => dialpad.context);
